import { Component, OnInit, signal } from '@angular/core';
import { WhitelabelService } from '../../services/whitelabel.service';
import { IWhitelabelVersionConfiguration, WhitelabelConfiguration } from '../../models/whitelabel-config';
import { WhitelabelAndroidConfigLogicAppPayload, WhitelabelAndroidDeploymentTracks, WhitelabelAndroidPayload } from '../../models/whitelabel-android-config';
import { WhitelabelIosConfigLogicAppPayload, WhitelabelIosDeploymentTracks, IWhitelabelIosPayload } from '../../models/whitelabel-ios-config';
import AzureService from 'src/app/shared/services/azure.service';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { WhitelabelRunComponent } from '../whitelabel-run/whitelabel-run.component';
import { BatchRunModalResponse, WhitelabelBatchRunComponent } from '../whitelabel-batch-run/whitelabel-batch-run.component';
import { ToastrService } from 'ngx-toastr';
import { Observable, forkJoin, of, switchMap, map, catchError } from 'rxjs';
import { AlphabeticalSortPipe } from '../../pipes/alphabetical-sort-whitelabel-apps.pipe';
import SearchFilterAppPipe from '../../pipes/search-filter-whitelabel-app.pipe';
import { SortWhitelabelAppsByTypePipe } from '../../pipes/sort-whitelabel-apps-by-type.pipe';

interface BatchRunResult {
  accountantId: string;
  success: boolean;
}

interface RunAppModalResponse {
  tracks: {
    ios: WhitelabelIosDeploymentTracks,
    android: WhitelabelAndroidDeploymentTracks
  },
  versions: IWhitelabelVersionConfiguration,
  releaseNotes: string;
}

interface ListFilterMode {
  mode: {
    label: string,
    value: ListFilterModeAllowedValues
  }
}

export type ListFilterModeAllowedValues = 'all' | 'inDraft' | 'created'

@Component({
  standalone: false,
  selector: 'app-whitelabel-apps-list',
  templateUrl: './whitelabel-apps-list.component.html',
  styleUrls: ['./whitelabel-apps-list.component.css']
})
export class WhitelabelAppsListComponent implements OnInit {

  public readonly accountantConfigurations = signal<WhitelabelConfiguration[]>([]);

  public readonly loading = signal(false);

  public searchBarContent: string | null = null;

  public selectedAppIds = new Set<string>();

  private searchFilterPipe = new SearchFilterAppPipe();
  private alphabeticalSortPipe = new AlphabeticalSortPipe();
  private sortByTypePipe = new SortWhitelabelAppsByTypePipe();

  public listFilterMode: ListFilterMode[] = [
    {
      mode: {
        label: 'Todos',
        value: 'all'
      }
    },
    {
      mode: {
        label: 'Criados',
        value: 'created'
      }
    },
    {
      mode: {
        label: 'Em Rascunho',
        value: 'inDraft'
      }
    }
  ]

  public selectedFilter!: ListFilterMode;

  constructor(
    private whitelabelService: WhitelabelService,
    private azureService: AzureService,
    private modalService: MatDialog,
    private toastrService: ToastrService) {

      const filterMode = this.listFilterMode.find(f => f.mode.value === 'all');
      if (filterMode) {
        this.selectedFilter = filterMode;
      }
  }

  ngOnInit(): void {
    this.loading.set(true);

    this.whitelabelService.getConfigurations().subscribe({
      next: (configs) => {
        this.accountantConfigurations.set(configs);
        this.loading.set(false);
      },
      error: (_) => {
        this.loading.set(false);
        this.toastrService.error('Ocorreu um erro na sua requisição');
      }
    })
  }

  public isValidDate(date: any): boolean {
    if (!date) {
      return true;
    }
  
    const isStringWithLetters = /[a-zA-Z]/.test(date);
    if (isStringWithLetters) {
      return false; 
    }
  
    return !isNaN(Date.parse(date));
  }   

  public changeFilter(value: ListFilterModeAllowedValues) {
    const filterWithThisMode = this.listFilterMode.find(f => f.mode.value === value)
    if (!filterWithThisMode) return;

    this.selectedFilter = filterWithThisMode;
  }

  /**
   * Lista exibida na tabela aplicando os mesmos filtros/ordenação do template,
   * usada para resolver a seleção de "selecionar todos".
   */
  public get displayedConfigurations(): WhitelabelConfiguration[] {
    const configurations = this.accountantConfigurations();
    if (!configurations.length) return [];

    const filtered = this.searchFilterPipe.transform(configurations, this.searchBarContent);
    const sorted = this.alphabeticalSortPipe.transform(filtered);
    return this.sortByTypePipe.transform(sorted, this.selectedFilter.mode.value);
  }

  /** Apps que podem ter o "play" acionado (mesma condição de exibição do botão). */
  public isSelectable(appConfig: WhitelabelConfiguration): boolean {
    return !appConfig.isDraft && (appConfig.hasIos() || appConfig.hasAndroid());
  }

  public isSelected(appConfig: WhitelabelConfiguration): boolean {
    return this.selectedAppIds.has(appConfig.accountantId);
  }

  public toggleSelection(appConfig: WhitelabelConfiguration): void {
    if (this.selectedAppIds.has(appConfig.accountantId)) {
      this.selectedAppIds.delete(appConfig.accountantId);
    } else {
      this.selectedAppIds.add(appConfig.accountantId);
    }
  }

  public get selectedCount(): number {
    return this.selectedAppIds.size;
  }

  private get selectableDisplayedConfigurations(): WhitelabelConfiguration[] {
    return this.displayedConfigurations.filter(c => this.isSelectable(c));
  }

  public get allDisplayedSelected(): boolean {
    const selectable = this.selectableDisplayedConfigurations;
    return selectable.length > 0 && selectable.every(c => this.selectedAppIds.has(c.accountantId));
  }

  public toggleSelectAll(): void {
    const selectable = this.selectableDisplayedConfigurations;

    if (this.allDisplayedSelected) {
      selectable.forEach(c => this.selectedAppIds.delete(c.accountantId));
    } else {
      selectable.forEach(c => this.selectedAppIds.add(c.accountantId));
    }
  }

  public clearSelection(): void {
    this.selectedAppIds.clear();
  }

  public onRunBatch(): void {
    const selectedConfigs = this.accountantConfigurations().filter(c => this.selectedAppIds.has(c.accountantId));
    if (selectedConfigs.length === 0) return;

    this.modalService.open(WhitelabelBatchRunComponent, {
      width: "30%",
      data: {
        configurations: selectedConfigs
      }
    }).afterClosed().subscribe({
      next: (res: BatchRunModalResponse) => {
        if (!res) return;
        this.runBatch(selectedConfigs, res);
      }
    });
  }

  private runBatch(configs: WhitelabelConfiguration[], res: BatchRunModalResponse): void {
    this.toastrService.info(`Iniciando o envio de ${configs.length} app(s) em paralelo...`);

    const runs = configs.map(appConfig => this.buildAppRun(appConfig, res));

    forkJoin(runs).subscribe({
      next: (results) => {
        const succeeded = results.filter(r => r.success).length;
        const failed = results.length - succeeded;

        if (failed === 0) {
          this.toastrService.success(`${succeeded} app(s) enviado(s) com sucesso`);
        } else if (succeeded === 0) {
          this.toastrService.error(`Falha ao enviar ${failed} app(s)`);
        } else {
          this.toastrService.warning(`${succeeded} app(s) enviado(s) com sucesso, ${failed} com falha`);
        }

        this.selectedAppIds.clear();
      },
      error: (_) => {
        this.toastrService.error('Ocorreu um erro ao enviar os apps');
      }
    });
  }

  private buildAppRun(appConfig: WhitelabelConfiguration, res: BatchRunModalResponse): Observable<BatchRunResult> {
    const nextVersionCode = String(Number(appConfig.versions?.versionCode ?? 0) + 1);
    const versionName = res.versionName ?? appConfig.versions?.versionName ?? '1.0.0';

    const configuration = new WhitelabelConfiguration({...appConfig, versions: {
      versionCode: nextVersionCode,
      versionName: versionName,
    }});

    return this.whitelabelService.saveConfigurations(configuration).pipe(
      switchMap(config => {
        const savedConfig = this.accountantConfigurations().find(c => c.accountantId === config.accountantId);
        if (savedConfig) {
          savedConfig.versions = config.versions;
        }

        const triggers: Observable<any>[] = [];

        if (res.tracks.android && config.hasNecessaryParametersToRunAndroidLogicApp) {
          const androidPayload = new WhitelabelAndroidConfigLogicAppPayload({
            accountantId: config.accountantId,
            name: config.name,
            intentUrl: config.intentUrl,
            intentScheme: config.intentScheme,
            packageName: config.android.androidPackageName!,
            track: res.tracks.android,
            oneSignalAppId: config.oneSignal!.appId,
            releaseNotes: res.releaseNotes,
            versionCode: config.versions!.versionCode,
            versionName: config.versions!.versionName,
            serviceAccountPrivateKey: config.android.serviceAccountPrivateKey,
            serviceAccountEmail: config.android.serviceAccountEmail
          });

          triggers.push(this.runAndroidApp(config, androidPayload));
        }

        if (res.tracks.ios && config.hasNecessaryParameterersToRunIosLogicApp) {
          const iosPayload = new WhitelabelIosConfigLogicAppPayload({
            accountantId: config.accountantId,
            name: config.name,
            intentUrl: config.intentUrl,
            apiKeyId: config.ios.apiKeyId!,
            intentScheme: config.intentScheme,
            apiKeyIssuerId: config.ios.apiKeyIssuerId!,
            apiTokenBase64: config.ios.apiTokenBase64!,
            appId: config.ios.appId!,
            bundleIdentifier: config.ios.iosBundleId!,
            oneSignalAppId: config.oneSignal!.appId,
            track: res.tracks.ios,
            releaseNotes: res.releaseNotes,
            versionCode: config.versions!.versionCode,
            versionName: config.versions!.versionName,
          });

          triggers.push(this.runIosApp(config, iosPayload));
        }

        if (triggers.length === 0) {
          return of<BatchRunResult>({ accountantId: config.accountantId, success: false });
        }

        return forkJoin(triggers).pipe(
          map(() => ({ accountantId: config.accountantId, success: true }))
        );
      }),
      catchError(() => of<BatchRunResult>({ accountantId: appConfig.accountantId, success: false }))
    );
  }

  public onRun(appConfig: WhitelabelConfiguration) {
    this.modalService.open(WhitelabelRunComponent, {
      width: "30%",
      data: {
        configuration: appConfig
      }
    }).afterClosed().subscribe({
      next: (res: RunAppModalResponse) => {
        if (!res) return;

        const configuration = new WhitelabelConfiguration({...appConfig, versions: {
          versionCode: res.versions.versionCode,
          versionName: res.versions.versionName,
        }});

        this.whitelabelService.saveConfigurations(configuration).subscribe({
          next: (config) => {
            let savedConfig = this.accountantConfigurations().find(c => c.accountantId === config.accountantId);
            if (savedConfig) {
              savedConfig.versions = config.versions;
            }

            if (res.tracks.android && config.hasNecessaryParametersToRunAndroidLogicApp) {
              const androidPayload = new WhitelabelAndroidConfigLogicAppPayload({
                accountantId: config.accountantId,
                name: config.name,
                intentUrl: config.intentUrl,
                intentScheme: config.intentScheme,
                packageName: config.android.androidPackageName!,
                track: res.tracks.android,
                oneSignalAppId: config.oneSignal!.appId,
                releaseNotes: res.releaseNotes,
                versionCode: config.versions!.versionCode,
                versionName: config.versions!.versionName,
                serviceAccountPrivateKey: config.android.serviceAccountPrivateKey,
                serviceAccountEmail: config.android.serviceAccountEmail
              });

              this.runAndroidApp(config, androidPayload).subscribe();
            }

            if (res.tracks.ios && config.hasNecessaryParameterersToRunIosLogicApp) {
              const iosPayload = new WhitelabelIosConfigLogicAppPayload({
                accountantId: config.accountantId,
                name: config.name,
                intentUrl: config.intentUrl,
                apiKeyId: config.ios.apiKeyId!,
                intentScheme: config.intentScheme,
                apiKeyIssuerId: config.ios.apiKeyIssuerId!,
                apiTokenBase64: config.ios.apiTokenBase64!,
                appId: config.ios.appId!,
                bundleIdentifier: config.ios.iosBundleId!,
                oneSignalAppId: config.oneSignal!.appId,
                track: res.tracks.ios,
                releaseNotes: res.releaseNotes,
                versionCode: config.versions!.versionCode,
                versionName: config.versions!.versionName,
              });

              this.runIosApp(config, iosPayload).subscribe();
            }
          }
        });
      }
    });
  }

  private runAndroidApp(appConfig: WhitelabelConfiguration, androidPayload: WhitelabelAndroidConfigLogicAppPayload) {
    if (!appConfig.hasAndroid()) return new Observable();
    return this.azureService.triggerLogicApp<WhitelabelAndroidConfigLogicAppPayload>(environment.azure.whitelabel.androidLogicApp, androidPayload);
  }

  private runIosApp(appConfig: WhitelabelConfiguration, iosPayload: WhitelabelIosConfigLogicAppPayload) {
    if (!appConfig.hasIos()) return new Observable();
    return this.azureService.triggerLogicApp<WhitelabelIosConfigLogicAppPayload>(environment.azure.whitelabel.iosLogicApp, iosPayload);
  }
}
